import {defineStore} from 'pinia';
import {computed, ref, shallowRef} from 'vue';
import InternalChatUser from '@src/types/InternalChatUser';
import InternalMessage from '@src/types/InternalMessage';
import InternalChatService from '@src/services/internalChat';
import useStore from "@src/stores/store";

export const useInternalChatStore = defineStore('internalChat', () => {
    const users = ref<InternalChatUser[]>([]);
    const selectedUserId = shallowRef<number | null>(null);
    const messages = ref<InternalMessage[]>([]);
    const isLoadingUsers = shallowRef(false);
    const isLoadingMessages = shallowRef(false);
    const isSending = shallowRef(false);
    const hasMoreMessages = shallowRef(false);
    const currentPage = shallowRef(1);
    const lastPage = shallowRef(1);
    const unreadCounts = ref<Record<number, number>>({});
    const store = useStore();

    const totalUnread = computed(() =>
        Object.values(unreadCounts.value).filter(n => n > 0).length
    );

    const selectedUser = computed(() =>
        users.value.find(u => u.id === selectedUserId.value) ?? null
    );

    async function loadUsers(): Promise<void> {
        isLoadingUsers.value = true;
        try {
            users.value = await InternalChatService.getUsers();
            const counts: Record<number, number> = {};
            for (const user of users.value) {
                if (user.unread_count) counts[user.id] = user.unread_count;
            }
            unreadCounts.value = counts;
        } catch (e) {
            console.error('❌ erro ao carregar usuários do chat interno', e);
        } finally {
            isLoadingUsers.value = false;
        }
    }

    async function loadMessages(userId: number, page: number): Promise<void> {
        isLoadingMessages.value = true;
        try {
            const result = await InternalChatService.getMessages(userId, page);
            const ordered = [...result.data].reverse();

            if (page === 1) {
                messages.value = ordered;
            } else {
                messages.value = [...ordered, ...messages.value];
            }

            currentPage.value = result.meta.current_page;
            lastPage.value = result.meta.last_page;
            hasMoreMessages.value = result.meta.current_page < result.meta.last_page;
        } catch (e) {
            console.error('❌ erro ao carregar mensagens do chat interno', e);
        } finally {
            isLoadingMessages.value = false;
        }
    }

    async function selectUser(userId: number): Promise<void> {
        selectedUserId.value = userId;
        unreadCounts.value = {...unreadCounts.value, [userId]: 0};
        messages.value = [];
        currentPage.value = 1;
        InternalChatService.markRead(userId).catch(() => {
        });
        await loadMessages(userId, 1);
    }

    async function loadMoreMessages(): Promise<void> {
        if (!selectedUserId.value || !hasMoreMessages.value || isLoadingMessages.value) return;
        await loadMessages(selectedUserId.value, currentPage.value + 1);
    }

    async function sendMessage(body: string | null, file?: File | null): Promise<void> {
        if (!selectedUserId.value) return;
        isSending.value = true;
        try {
            const msg = await InternalChatService.sendMessage(selectedUserId.value, body, file);
            messages.value = [...messages.value, msg];
            updateUserLastMessage(msg);
        } catch (e) {
            console.error('❌ erro ao enviar mensagem do chat interno', e);
        } finally {
            isSending.value = false;
        }
    }

    function updateUserLastMessage(msg: InternalMessage): void {
        const authId = store.user?.id;
        const otherUserId = msg.sender_id === authId
            ? selectedUserId.value
            : msg.sender_id;

        const userIndex = users.value.findIndex(u => u.id === otherUserId);
        if (userIndex === -1) return;

        const updated = {...users.value[userIndex], messages: [msg, ...users.value[userIndex].messages]};
        const newUsers = [...users.value];
        newUsers.splice(userIndex, 1, updated);
        users.value = newUsers;
    }

    function clearSelection(): void {
        selectedUserId.value = null;
        messages.value = [];
    }

    function markMessagesAsRead(conversationId: number, readAt: string): void {
        const cutoff = new Date(readAt).getTime();
        messages.value = messages.value.map(msg => {
            if (
                msg.conversation_id === conversationId &&
                !msg.read_at &&
                new Date(msg.created_at).getTime() <= cutoff
            ) {
                return {...msg, read_at: readAt};
            }
            return msg;
        });
    }

    function receiveMessage(msg: InternalMessage): void {
        updateUserLastMessage(msg);

        if (msg.sender_id === selectedUserId.value) {
            messages.value = [...messages.value, msg];
        } else {
            unreadCounts.value = {
                ...unreadCounts.value,
                [msg.sender_id]: (unreadCounts.value[msg.sender_id] ?? 0) + 1,
            };
        }
    }

    return {
        users,
        selectedUserId,
        messages,
        isLoadingUsers,
        isLoadingMessages,
        isSending,
        hasMoreMessages,
        unreadCounts,
        totalUnread,
        selectedUser,
        loadUsers,
        selectUser,
        clearSelection,
        markMessagesAsRead,
        loadMoreMessages,
        sendMessage,
        receiveMessage,
    };
});
